import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { WorksheetItem, WorksheetType } from '@fgb/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Helper function to filter videos
function filterByVideo(items: WorksheetItem[]): WorksheetItem[] {
  return items.filter((item) => item.WorksheetType === WorksheetType.ProviderVideo && item.Active === 1);
}

@Component({
  selector: 'fgb-media-playlist',
  templateUrl: './media-playlist.component.html',
  styleUrl: './media-playlist.component.scss',
})
export class MediaPlaylistComponent {
  @Input({ transform: filterByVideo }) videos: WorksheetItem[] = [];
  @Output() playlistSelect = new EventEmitter<{ item: WorksheetItem; index: number }>();
  @Input() selectedIndex: number = 0;
  @Input() desktopVisibleItems: number = 3;
  @Input() mobileVisibleItems: number = 1.7;
  @Input() buttonTopPercent: number = 35;
  @Input() gap: number = 16;
  @ViewChild('carousel', { static: false }) carousel!: ElementRef<HTMLDivElement>;
  @ViewChild('track', { static: false }) track!: ElementRef<HTMLDivElement>;
  @ViewChildren('item') items!: QueryList<ElementRef<HTMLDivElement>>;

  index = 0;
  visibleItems = this.desktopVisibleItems;
  itemWidth = 0;
  isDesktop: boolean = true;
  isPrevDisabled: boolean = false;
  isNextDisabled: boolean = false;

  private mm = gsap.matchMedia();

  private startX = 0;
  private currentX = 0;
  private isDragging = false;
  private swipeThreshold = 50;
  private slideTween?: gsap.core.Tween;
  private isProgrammaticScroll = false;

  onPlaylistSelect(video: WorksheetItem, index: number): void {
    this.selectedIndex = index;

    const maxScrollIndex = Math.max(this.items.length - this.visibleItems, 0);
    const scrollIndex = Math.min(index, maxScrollIndex);

    this.slide(scrollIndex);
    this.playlistSelect.emit({ item: video, index: index });
  }

  ngAfterViewInit(): void {
    const itemEls = this.items.map((i) => i.nativeElement);

    this.mm.add(
      {
        isDesktop: '(min-width: 769px)',
        isMobile: '(max-width: 768px)',
      },
      (context) => {
        const { isDesktop } = context.conditions as any;
        this.isDesktop = isDesktop;

        this.visibleItems = isDesktop ? this.desktopVisibleItems : this.mobileVisibleItems;

        itemEls.forEach((itemEl) => {
          itemEl.style.flex = `0 0 calc((100% - ${(this.visibleItems - 1) * this.gap}px) / ${this.visibleItems})`;
        });

        this.updateSizes();
        this.slide(this.index);
        this.updateButtonState();
      }
    );

    this.addDragSupport();
    this.addScrollListener();
    window.addEventListener('resize', this.updateSizes);
  }

  updateSizes = () => {
    const firstItem = this.items.first?.nativeElement;
    if (!firstItem) return;

    this.itemWidth = firstItem.offsetWidth + this.gap;
  };

  slide(index: number): void {
    const maxIndex = Math.max(this.items.length - this.visibleItems, 0);
    const clampedIndex = Math.min(index, maxIndex);
    this.slideTween?.kill();
    this.isProgrammaticScroll = true;

    this.slideTween = gsap.to(this.carousel.nativeElement, {
      scrollLeft: clampedIndex * this.itemWidth,
      duration: 0.4,
      ease: 'power2.out',
      onComplete: () => {
        this.index = clampedIndex;
        this.isProgrammaticScroll = false;
        this.updateButtonState();
      },
    });
  }

  next(): void {
    const maxIndex = Math.max(this.items.length - this.visibleItems, 0);
    const index = Math.min(this.index + 1, maxIndex);
    this.slide(index);
  }

  prev(): void {
    const index = Math.max(this.index - 1, 0);
    this.slide(index);
  }

  addDragSupport(): void {
    const trackEl = this.track.nativeElement;

    trackEl.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);

    trackEl.style.cursor = 'grab';
    trackEl.addEventListener('pointerdown', () => (trackEl.style.cursor = 'grabbing'));
    window.addEventListener('pointerup', () => (trackEl.style.cursor = 'grab'));
  }

  onPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    if (e.pointerType === 'touch') return;

    this.isDragging = true;
    this.startX = e.clientX;
    this.currentX = e.clientX;
    this.slideTween?.kill();
  };

  onPointerMove = (e: PointerEvent) => {
    e.preventDefault();
    if (!this.isDragging) return;

    this.currentX = e.clientX;
    const delta = this.currentX - this.startX;

    this.carousel.nativeElement.scrollLeft = this.index * this.itemWidth - delta;
  };

  onPointerUp = (e: PointerEvent) => {
    e.preventDefault();
    if (!this.isDragging) return;

    const delta = this.currentX - this.startX;

    if (Math.abs(delta) > this.swipeThreshold) {
      delta < 0 ? this.next() : this.prev();
    } else {
      this.slide(this.index);
    }

    this.isDragging = false;
  };

  addScrollListener(): void {
    this.carousel.nativeElement.addEventListener('scroll', this.onScroll);
  }

  onScroll = () => {
    if (this.isProgrammaticScroll) return;
    const scrollLeft = this.carousel.nativeElement.scrollLeft;
    this.index = Math.min(Math.round(scrollLeft / this.itemWidth), Math.max(this.items.length - this.visibleItems, 0));
    this.updateButtonState();
  };

  ngOnDestroy(): void {
    const trackEl = this.track.nativeElement;

    trackEl.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.carousel.nativeElement.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.updateSizes);

    this.mm.kill();
  }

  private updateButtonState(): void {
    if (!this.isDesktop) {
      this.isPrevDisabled = true;
      this.isNextDisabled = true;
      return;
    }
    if (this.index <= 0) {
      this.isPrevDisabled = true;
    } else {
      this.isPrevDisabled = false;
    }
    if (this.index >= this.items?.length - this.visibleItems) {
      this.isNextDisabled = true;
    } else {
      this.isNextDisabled = false;
    }
  }
}
